import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, BarChart3, ArrowUp, ArrowDown, ArrowUpDown, User, Users, Sigma } from 'lucide-react';
import { formatStat } from '@/lib/sportStats';
import { StatLegend } from '@/components/stats/StatLegend';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAYER_COLS = [
  { key: 'rank',      label: '#',      align: 'center', sortable: false, isRank: true },
  { key: 'name',      label: 'Player', align: 'left',   sortable: true,  type: 'string' },
  { key: 'team_name', label: 'Team',   align: 'left',   sortable: true,  type: 'string' },
  { key: 'gp',        label: 'GP',     align: 'center', sortable: true,  type: 'number' },
  { key: 'ppg',       label: 'PPG',    align: 'center', sortable: true,  type: 'number', highlight: true },
  { key: 'rpg',       label: 'RPG',    align: 'center', sortable: true,  type: 'number' },
  { key: 'apg',       label: 'APG',    align: 'center', sortable: true,  type: 'number' },
  { key: 'spg',       label: 'SPG',    align: 'center', sortable: true,  type: 'number' },
  { key: 'bpg',       label: 'BPG',    align: 'center', sortable: true,  type: 'number' },
  { key: 'topg',      label: 'TOPG',   align: 'center', sortable: true,  type: 'number' },
  { key: 'fg_pct',    label: 'FG%',    align: 'center', sortable: true,  type: 'number' },
  { key: 'three_pct', label: '3P%',    align: 'center', sortable: true,  type: 'number' },
  { key: 'ft_pct',    label: 'FT%',    align: 'center', sortable: true,  type: 'number' },
  { key: 'per',       label: 'PER',    align: 'center', sortable: true,  type: 'number', highlight: true },
];

// Cumulative (season totals) — separates OREB / DREB and uses absolute counts
// instead of per-game averages. Sorted by total points by default.
const CUMULATIVE_COLS = [
  { key: 'rank',          label: '#',     align: 'center', sortable: false, isRank: true },
  { key: 'name',          label: 'Player', align: 'left',  sortable: true,  type: 'string' },
  { key: 'team_name',     label: 'Team',   align: 'left',  sortable: true,  type: 'string' },
  { key: 'gp',            label: 'GP',    align: 'center', sortable: true,  type: 'number' },
  { key: 'total_pts',     label: 'PTS',   align: 'center', sortable: true,  type: 'number', highlight: true },
  { key: 'total_fgm',     label: 'FGM',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_fga',     label: 'FGA',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_three_m', label: '3PM',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_three_a', label: '3PA',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_ftm',     label: 'FTM',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_fta',     label: 'FTA',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_oreb',    label: 'OREB',  align: 'center', sortable: true,  type: 'number' },
  { key: 'total_dreb',    label: 'DREB',  align: 'center', sortable: true,  type: 'number' },
  { key: 'total_reb',     label: 'REB',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_ast',     label: 'AST',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_stl',     label: 'STL',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_blk',     label: 'BLK',   align: 'center', sortable: true,  type: 'number' },
  { key: 'total_to',      label: 'TO',    align: 'center', sortable: true,  type: 'number' },
];

const TEAM_COLS = [
  { key: 'rank',      label: '#',     align: 'center', sortable: false, isRank: true },
  { key: 'team_name', label: 'Team',  align: 'left',   sortable: true,  type: 'string' },
  { key: 'gp',        label: 'GP',    align: 'center', sortable: true,  type: 'number' },
  { key: 'ppg',       label: 'PPG',   align: 'center', sortable: true,  type: 'number', highlight: true },
  { key: 'opp_ppg',   label: 'OPP',   align: 'center', sortable: true,  type: 'number' },
  { key: 'net',       label: 'NET',   align: 'center', sortable: true,  type: 'number', highlight: true },
  { key: 'rpg',       label: 'RPG',   align: 'center', sortable: true,  type: 'number' },
  { key: 'apg',       label: 'APG',   align: 'center', sortable: true,  type: 'number' },
  { key: 'spg',       label: 'SPG',   align: 'center', sortable: true,  type: 'number' },
  { key: 'bpg',       label: 'BPG',   align: 'center', sortable: true,  type: 'number' },
  { key: 'topg',      label: 'TOPG',  align: 'center', sortable: true,  type: 'number' },
  { key: 'fg_pct',    label: 'FG%',   align: 'center', sortable: true,  type: 'number' },
  { key: 'three_pct', label: '3P%',   align: 'center', sortable: true,  type: 'number' },
  { key: 'ft_pct',    label: 'FT%',   align: 'center', sortable: true,  type: 'number' },
];

const careerHref = (leaderboardToken, teamName, playerName, eventId) => {
  if (!leaderboardToken) return null;
  const params = new URLSearchParams({ team: teamName, name: playerName });
  if (eventId) params.append('event_id', eventId);
  return `/stats/leaderboard/${leaderboardToken}/career?${params.toString()}`;
};

function sortRows(rows, cols, sortKey, sortDir) {
  if (!rows?.length) return [];
  const col = cols.find(c => c.key === sortKey);
  const dir = sortDir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (col?.type === 'string') {
      return ((av || '').toString().localeCompare((bv || '').toString())) * dir;
    }
    return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
  });
}

function SortableHeader({ cols, sortKey, sortDir, onClick, testidPrefix }) {
  return (
    <TableHeader>
      <TableRow className="bg-slate-50 border-b-2 border-slate-200 hover:bg-slate-50">
        {cols.map(c => {
          const active = sortKey === c.key;
          const Icon = !c.sortable ? null : (active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown);
          return (
            <TableHead
              key={c.key}
              onClick={() => onClick(c.key)}
              className={`px-3 py-2 text-${c.align} text-[10px] uppercase tracking-wide font-bold ${c.sortable ? 'cursor-pointer hover:bg-slate-100 select-none' : ''} ${active ? 'text-[#00247D]' : 'text-slate-500'}`}
              data-testid={`${testidPrefix}-col-${c.key}`}
            >
              <span className="inline-flex items-center gap-1 justify-center">
                {c.label}
                {Icon && <Icon className={`w-3 h-3 ${active ? 'text-[#00247D]' : 'text-slate-300'}`} />}
              </span>
            </TableHead>
          );
        })}
      </TableRow>
    </TableHeader>
  );
}

function PlayerStatsTable({ rows, sortKey, sortDir, onSort, leaderboardShareToken, eventId, teamLink }) {
  const sorted = useMemo(() => sortRows(rows, PLAYER_COLS, sortKey, sortDir), [rows, sortKey, sortDir]);
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="w-full text-sm" data-testid="season-stats-player-table">
          <SortableHeader cols={PLAYER_COLS} sortKey={sortKey} sortDir={sortDir} onClick={onSort} testidPrefix="season-stats" />
          <TableBody>
            {sorted.map((p, i) => {
              const cHref = careerHref(leaderboardShareToken, p.team_name, p.name, eventId);
              const tHref = teamLink(p.team_name);
              return (
                <TableRow key={`${p.name}-${p.team_name}-${i}`} className="border-b border-slate-100 hover:bg-amber-50/40">
                  <TableCell className="px-3 py-2 text-center font-bold text-slate-500">{i + 1}</TableCell>
                  <TableCell className="px-3 py-2 font-semibold text-slate-800">
                    {cHref ? (
                      <Link to={cHref} className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                        data-testid={`season-stats-player-${i}`}>
                        {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{p.name}</span>
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{p.name}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-slate-500">
                    {tHref ? (
                      <Link to={tHref} className="inline-flex items-center gap-1.5 hover:text-amber-600 hover:underline underline-offset-2">
                        {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                        {p.team_name}
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                        {p.team_name}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.gp}</TableCell>
                  <TableCell className="px-3 py-2 text-center font-bold text-[#00247D]">{p.ppg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.rpg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.apg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.spg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.bpg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-rose-500/80">{p.topg ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.fg_pct}%</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.three_pct ?? 0}%</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.ft_pct ?? 0}%</TableCell>
                  <TableCell className="px-3 py-2 text-center font-black text-amber-500">{p.per}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function CumulativeStatsTable({ rows, sortKey, sortDir, onSort, leaderboardShareToken, eventId, teamLink }) {
  const sorted = useMemo(() => sortRows(rows, CUMULATIVE_COLS, sortKey, sortDir), [rows, sortKey, sortDir]);
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="w-full text-sm" data-testid="season-stats-cumulative-table">
          <SortableHeader cols={CUMULATIVE_COLS} sortKey={sortKey} sortDir={sortDir} onClick={onSort} testidPrefix="season-cumulative-stats" />
          <TableBody>
            {sorted.map((p, i) => {
              const cHref = careerHref(leaderboardShareToken, p.team_name, p.name, eventId);
              const tHref = teamLink(p.team_name);
              return (
                <TableRow key={`cum-${p.name}-${p.team_name}-${i}`} className="border-b border-slate-100 hover:bg-amber-50/40">
                  <TableCell className="px-3 py-2 text-center font-bold text-slate-500">{i + 1}</TableCell>
                  <TableCell className="px-3 py-2 font-semibold text-slate-800">
                    {cHref ? (
                      <Link to={cHref} className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                        data-testid={`season-cumulative-stats-player-${i}`}>
                        {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{p.name}</span>
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{p.name}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-slate-500">
                    {tHref ? (
                      <Link to={tHref} className="inline-flex items-center gap-1.5 hover:text-amber-600 hover:underline underline-offset-2">
                        {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                        {p.team_name}
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                        {p.team_name}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.gp}</TableCell>
                  <TableCell className="px-3 py-2 text-center font-bold text-[#00247D]">{p.total_pts ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_fgm ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.total_fga ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_three_m ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.total_three_a ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_ftm ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{p.total_fta ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_oreb ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_dreb ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center font-semibold text-slate-800">{p.total_reb ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_ast ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_stl ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{p.total_blk ?? 0}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-rose-500/80">{p.total_to ?? 0}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function TeamStatsTable({ rows, sortKey, sortDir, onSort, teamLink }) {
  const sorted = useMemo(() => sortRows(rows, TEAM_COLS, sortKey, sortDir), [rows, sortKey, sortDir]);
  return (
    <Card className="border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="w-full text-sm" data-testid="season-stats-team-table">
          <SortableHeader cols={TEAM_COLS} sortKey={sortKey} sortDir={sortDir} onClick={onSort} testidPrefix="season-team-stats" />
          <TableBody>
            {sorted.map((t, i) => {
              const tHref = teamLink(t.team_name);
              const netClass = (t.net ?? 0) > 0 ? 'text-emerald-600' : (t.net ?? 0) < 0 ? 'text-rose-500' : 'text-slate-500';
              return (
                <TableRow key={`${t.team_name}-${i}`} className="border-b border-slate-100 hover:bg-amber-50/40">
                  <TableCell className="px-3 py-2 text-center font-bold text-slate-500">{i + 1}</TableCell>
                  <TableCell className="px-3 py-2 font-semibold text-slate-800">
                    {tHref ? (
                      <Link to={tHref} className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                        data-testid={`season-team-stats-row-${i}`}>
                        {t.team_logo && <img src={`${API_URL}/api/files/${t.team_logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{t.team_name}</span>
                      </Link>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {t.team_logo && <img src={`${API_URL}/api/files/${t.team_logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                        <span>{t.team_name}</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{t.gp}</TableCell>
                  <TableCell className="px-3 py-2 text-center font-bold text-[#00247D]">{t.ppg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-600">{t.opp_ppg}</TableCell>
                  <TableCell className={`px-3 py-2 text-center font-bold ${netClass}`}>{t.net > 0 ? `+${t.net}` : t.net}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{t.rpg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{t.apg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{t.spg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-700">{t.bpg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-rose-500/80">{t.topg}</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{t.fg_pct}%</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{t.three_pct}%</TableCell>
                  <TableCell className="px-3 py-2 text-center text-slate-500">{t.ft_pct}%</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Non-basketball sports — dynamic tables driven by the sport definition
// (Netball / Soccer / Volleyball). Reads `/api/events/{event_id}/leaderboard`
// which returns per_rankings + team_rankings with `totals[stat_key]` and
// `averages[stat_key]` bags keyed by every stat the sport tracks.
// --------------------------------------------------------------------------
function SportSeasonStatsTables({ eventId, leaderboardShareToken, teamLink }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('player'); // 'player' | 'team'

  useEffect(() => {
    if (!eventId) return;
    setLoading(true);
    const token = localStorage.getItem('session_token');
    fetch(`${API_URL}/api/events/${eventId}/leaderboard`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  const definition = data?.sport_definition;
  const stats = useMemo(() => (definition?.stats || []), [definition]);
  const primary = data?.primary_stat;

  // Dynamic columns: rank · name/team · GP · one column per tracked stat.
  const playerCols = useMemo(() => ([
    { key: 'rank', label: '#', align: 'center', sortable: false, isRank: true },
    { key: 'name', label: 'Player', align: 'left', sortable: true, type: 'string' },
    { key: 'team_name', label: 'Team', align: 'left', sortable: true, type: 'string' },
    { key: 'games_played', label: 'GP', align: 'center', sortable: true, type: 'number' },
    ...stats.map(s => ({
      key: `total_${s.key}`,
      label: s.short || (s.label || s.key).toUpperCase(),
      align: 'center', sortable: true, type: 'number',
      highlight: s.key === primary,
    })),
  ]), [stats, primary]);

  const teamCols = useMemo(() => ([
    { key: 'rank', label: '#', align: 'center', sortable: false, isRank: true },
    { key: 'team_name', label: 'Team', align: 'left', sortable: true, type: 'string' },
    { key: 'games_played', label: 'GP', align: 'center', sortable: true, type: 'number' },
    ...stats.map(s => ({
      key: `total_${s.key}`,
      label: s.short || (s.label || s.key).toUpperCase(),
      align: 'center', sortable: true, type: 'number',
      highlight: s.key === primary,
    })),
  ]), [stats, primary]);

  const defaultKey = primary ? `total_${primary}` : 'games_played';
  const [playerSortKey, setPlayerSortKey] = useState(defaultKey);
  const [playerSortDir, setPlayerSortDir] = useState('desc');
  const [teamSortKey, setTeamSortKey] = useState(defaultKey);
  const [teamSortDir, setTeamSortDir] = useState('desc');

  // Flatten totals bag so sortRows can access `total_<stat>` directly.
  const playerRows = useMemo(() => (
    (data?.per_rankings || []).map(p => ({
      ...p,
      name: p.name,
      team_name: p.team_name,
      games_played: p.games_played,
      ...Object.fromEntries((stats || []).map(s => [`total_${s.key}`, p.totals?.[s.key] ?? 0])),
    }))
  ), [data, stats]);

  const teamRows = useMemo(() => (
    (data?.team_rankings || []).map(t => ({
      ...t,
      team_name: t.team_name,
      games_played: t.games_played,
      ...Object.fromEntries((stats || []).map(s => [`total_${s.key}`, t.totals?.[s.key] ?? 0])),
    }))
  ), [data, stats]);

  if (loading) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="sport-season-stats-loading">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
        </CardContent>
      </Card>
    );
  }

  if (!data || (data.games_count || 0) === 0 || stats.length === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="sport-season-stats-empty">
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold mb-1">No season stats yet</p>
          <p className="text-slate-500 text-sm">Once box scores are saved, every player and team will appear here.</p>
        </CardContent>
      </Card>
    );
  }

  const onPlayerSort = (key) => {
    const col = playerCols.find(c => c.key === key);
    if (!col?.sortable) return;
    if (playerSortKey === key) setPlayerSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setPlayerSortKey(key); setPlayerSortDir(col.type === 'string' ? 'asc' : 'desc'); }
  };
  const onTeamSort = (key) => {
    const col = teamCols.find(c => c.key === key);
    if (!col?.sortable) return;
    if (teamSortKey === key) setTeamSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTeamSortKey(key); setTeamSortDir(col.type === 'string' ? 'asc' : 'desc'); }
  };

  const sortedPlayerRows = sortRows(playerRows, playerCols, playerSortKey, playerSortDir);
  const sortedTeamRows = sortRows(teamRows, teamCols, teamSortKey, teamSortDir);

  const careerHrefFor = (playerName, teamName) => {
    if (!leaderboardShareToken) return null;
    const params = new URLSearchParams({ team: teamName, name: playerName });
    if (eventId) params.append('event_id', eventId);
    return `/stats/leaderboard/${leaderboardShareToken}/career?${params.toString()}`;
  };

  const ToggleBtn = ({ value, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      data-testid={`sport-season-stats-toggle-${value}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-md transition-colors
        ${mode === value
          ? 'bg-[#00247D] text-white shadow-sm'
          : 'bg-white text-slate-500 hover:text-[#00247D] hover:bg-slate-100 border border-slate-200'}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="sport-season-stats">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-[#00247D] flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Season Stats · {definition?.label || 'Sport'}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100/70 p-1 rounded-lg" data-testid="sport-season-stats-mode-toggle">
            <ToggleBtn value="player" icon={User}  label="Player Totals" />
            <ToggleBtn value="team"   icon={Users} label="Team Totals" />
          </div>
          <p className="text-xs text-slate-500 italic">
            Click any column to sort · {data.games_count} match{data.games_count !== 1 ? 'es' : ''}
          </p>
        </div>
      </div>

      {mode === 'player' ? (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="w-full text-sm" data-testid="sport-season-stats-player-table">
              <SortableHeader cols={playerCols} sortKey={playerSortKey} sortDir={playerSortDir} onClick={onPlayerSort} testidPrefix="sport-season-stats-player" />
              <TableBody>
                {sortedPlayerRows.map((p, i) => {
                  const cHref = careerHrefFor(p.name, p.team_name);
                  const tHref = teamLink(p.team_name);
                  return (
                    <TableRow key={`${p.name}-${p.team_name}-${i}`} className="border-b border-slate-100 hover:bg-amber-50/40">
                      <TableCell className="px-3 py-2 text-center font-bold text-slate-500">{i + 1}</TableCell>
                      <TableCell className="px-3 py-2 font-semibold text-slate-800">
                        {cHref ? (
                          <Link to={cHref} className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                            data-testid={`sport-season-stats-player-row-${i}`}>
                            {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                            <span>{p.name}</span>
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            {p.headshot_path && <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                            <span>{p.name}</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-3 py-2 text-slate-500">
                        {tHref ? (
                          <Link to={tHref} className="inline-flex items-center gap-1.5 hover:text-amber-600 hover:underline underline-offset-2">
                            {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                            {p.team_name}
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            {p.team_logo && <img src={`${API_URL}/api/files/${p.team_logo}`} alt="" className="w-4 h-4 rounded-full object-cover" onError={e => { e.target.style.display = 'none'; }} />}
                            {p.team_name}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-3 py-2 text-center text-slate-500">{p.games_played}</TableCell>
                      {stats.map(s => (
                        <TableCell
                          key={s.key}
                          className={`px-3 py-2 text-center ${s.key === primary ? 'font-bold text-[#00247D]' : 'text-slate-700'}`}
                        >
                          {formatStat(s.key, p[`total_${s.key}`] ?? 0)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="w-full text-sm" data-testid="sport-season-stats-team-table">
              <SortableHeader cols={teamCols} sortKey={teamSortKey} sortDir={teamSortDir} onClick={onTeamSort} testidPrefix="sport-season-stats-team" />
              <TableBody>
                {sortedTeamRows.map((t, i) => {
                  const tHref = teamLink(t.team_name);
                  return (
                    <TableRow key={`${t.team_name}-${i}`} className="border-b border-slate-100 hover:bg-amber-50/40">
                      <TableCell className="px-3 py-2 text-center font-bold text-slate-500">{i + 1}</TableCell>
                      <TableCell className="px-3 py-2 font-semibold text-slate-800">
                        {tHref ? (
                          <Link to={tHref} className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                            data-testid={`sport-season-stats-team-row-${i}`}>
                            {t.team_logo && <img src={`${API_URL}/api/files/${t.team_logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                            <span>{t.team_name}</span>
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            {t.team_logo && <img src={`${API_URL}/api/files/${t.team_logo}`} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />}
                            <span>{t.team_name}</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="px-3 py-2 text-center text-slate-500">{t.games_played}</TableCell>
                      {stats.map(s => (
                        <TableCell
                          key={s.key}
                          className={`px-3 py-2 text-center ${s.key === primary ? 'font-bold text-[#00247D]' : 'text-slate-700'}`}
                        >
                          {formatStat(s.key, t[`total_${s.key}`] ?? 0)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      <StatLegend sport={data?.sport} codes={['GP', ...stats.map((s) => s.short || s.label)]} testId="season-stats-legend" />
    </div>
  );
}

export function PublicSeasonStats({ shareToken, eventId, leaderboardShareToken, eventTeams = [], sport = 'basketball' }) {
  const teamIdByName = useMemo(() => {
    const m = {};
    (eventTeams || []).forEach(t => {
      const nm = (t.name || '').trim().toLowerCase();
      if (nm) m[nm] = t.id;
    });
    return m;
  }, [eventTeams]);

  const teamLink = (teamName) => {
    if (!shareToken || !teamName) return null;
    const tid = teamIdByName[teamName.trim().toLowerCase()];
    return tid ? `/share/${shareToken}/team/${tid}` : null;
  };

  const isBasketball = (sport || 'basketball').toLowerCase() === 'basketball';
  if (!isBasketball) {
    return (
      <SportSeasonStatsTables
        eventId={eventId}
        leaderboardShareToken={leaderboardShareToken}
        teamLink={teamLink}
      />
    );
  }

  return (
    <BasketballSeasonStats
      shareToken={shareToken}
      eventId={eventId}
      leaderboardShareToken={leaderboardShareToken}
      teamLink={teamLink}
    />
  );
}

// --------------------------------------------------------------------------
// Basketball season stats — original 3-tab UI (Player Averages / Cumulative /
// Teams). Extracted so the sport-aware wrapper above can branch cleanly.
// --------------------------------------------------------------------------
function BasketballSeasonStats({ shareToken, eventId, leaderboardShareToken, teamLink }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('player'); // 'player' | 'cumulative' | 'team'
  // Default sorts: players → PER desc, cumulative → total PTS desc, teams → PPG desc.
  const [playerSortKey, setPlayerSortKey] = useState('per');
  const [playerSortDir, setPlayerSortDir] = useState('desc');
  const [cumulativeSortKey, setCumulativeSortKey] = useState('total_pts');
  const [cumulativeSortDir, setCumulativeSortDir] = useState('desc');
  const [teamSortKey, setTeamSortKey] = useState('ppg');
  const [teamSortDir, setTeamSortDir] = useState('desc');

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/share/${shareToken}/leaderboard?event_id=${encodeURIComponent(eventId || '')}`)
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [shareToken, eventId]);

  const playerRows = useMemo(() => (
    (data?.per_rankings || []).map(p => ({
      ...p,
      topg: p.topg ?? p.tov_per_game ?? 0,
    }))
  ), [data]);

  const teamRows = useMemo(() => data?.team_rankings || [], [data]);

  const onPlayerSort = (key) => {
    const col = PLAYER_COLS.find(c => c.key === key);
    if (!col?.sortable) return;
    if (playerSortKey === key) setPlayerSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setPlayerSortKey(key); setPlayerSortDir(col.type === 'string' ? 'asc' : 'desc'); }
  };
  const onCumulativeSort = (key) => {
    const col = CUMULATIVE_COLS.find(c => c.key === key);
    if (!col?.sortable) return;
    if (cumulativeSortKey === key) setCumulativeSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCumulativeSortKey(key); setCumulativeSortDir(col.type === 'string' ? 'asc' : 'desc'); }
  };
  const onTeamSort = (key) => {
    const col = TEAM_COLS.find(c => c.key === key);
    if (!col?.sortable) return;
    if (teamSortKey === key) setTeamSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTeamSortKey(key); setTeamSortDir(col.type === 'string' ? 'asc' : 'desc'); }
  };

  if (loading) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="season-stats-loading">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00247D]" />
        </CardContent>
      </Card>
    );
  }
  const empty = !data || (data.games_count || 0) === 0
    || (mode === 'player'
        ? playerRows.length === 0
        : mode === 'cumulative'
        ? playerRows.length === 0
        : teamRows.length === 0);
  if (empty && (data?.games_count || 0) === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="season-stats-empty">
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-semibold mb-1">No season stats yet</p>
          <p className="text-slate-500 text-sm">Once box scores are imported or live games are tracked, every player and team will appear here.</p>
        </CardContent>
      </Card>
    );
  }

  const ToggleBtn = ({ value, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      data-testid={`season-stats-toggle-${value}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-md transition-colors
        ${mode === value
          ? 'bg-[#00247D] text-white shadow-sm'
          : 'bg-white text-slate-500 hover:text-[#00247D] hover:bg-slate-100 border border-slate-200'}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="public-season-stats">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-[#00247D] flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Season Stats
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-100/70 p-1 rounded-lg" data-testid="season-stats-mode-toggle">
            <ToggleBtn value="player"     icon={User}   label="Player Averages" />
            <ToggleBtn value="cumulative" icon={Sigma}  label="Cumulative" />
            <ToggleBtn value="team"       icon={Users}  label="Teams" />
          </div>
          <p className="text-xs text-slate-500 italic">Click any column to sort · {data?.games_count || 0} game{data?.games_count !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {mode === 'player' ? (
        playerRows.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="season-stats-empty-players">
            <CardContent className="py-12 text-center text-slate-500 text-sm">No player stats recorded yet.</CardContent>
          </Card>
        ) : (
          <PlayerStatsTable
            rows={playerRows}
            sortKey={playerSortKey} sortDir={playerSortDir} onSort={onPlayerSort}
            leaderboardShareToken={leaderboardShareToken} eventId={eventId} teamLink={teamLink}
          />
        )
      ) : mode === 'cumulative' ? (
        playerRows.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="season-stats-empty-cumulative">
            <CardContent className="py-12 text-center text-slate-500 text-sm">No cumulative stats recorded yet.</CardContent>
          </Card>
        ) : (
          <CumulativeStatsTable
            rows={playerRows}
            sortKey={cumulativeSortKey} sortDir={cumulativeSortDir} onSort={onCumulativeSort}
            leaderboardShareToken={leaderboardShareToken} eventId={eventId} teamLink={teamLink}
          />
        )
      ) : (
        teamRows.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-slate-50" data-testid="season-stats-empty-teams">
            <CardContent className="py-12 text-center text-slate-500 text-sm">No team stats recorded yet.</CardContent>
          </Card>
        ) : (
          <TeamStatsTable
            rows={teamRows}
            sortKey={teamSortKey} sortDir={teamSortDir} onSort={onTeamSort}
            teamLink={teamLink}
          />
        )
      )}
    </div>
  );
}
